import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  ArrowRight, Heart, Activity, Scale, User,
  ChevronRight, Check, Clock
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";

const FITNESS_LEVELS = [
  { value: "beginner", icon: "🌱" },
  { value: "intermediate", icon: "💪" },
  { value: "advanced", icon: "🔥" },
  { value: "athlete", icon: "🏆" },
];

const ACTIVITY_LEVELS = [
  { value: "sedentary" },
  { value: "light" },
  { value: "moderate" },
  { value: "active" },
  { value: "veryActive" },
];

const HEALTH_CONDITIONS = [
  "none", "backPain", "jointIssues", "heartCondition",
  "asthma", "diabetes", "highBloodPressure", "other"
];

const GENDERS = ["male", "female", "other", "preferNotToSay"];

const HealthAssessment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    height_cm: "",
    weight_kg: "",
    age: "",
    gender: "prefer_not_to_say",
    fitness_level: "beginner",
    activity_frequency: "moderate",
    health_conditions: [],
    goals: [],
    reminder_time: "09:00",
  });
  const [bmiResult, setBmiResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(false);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await axios.get(`${API}/health-assessment`);
        if (res.data.assessment) {
          setExisting(true);
          const a = res.data.assessment;
          setForm({
            height_cm: a.height_cm || "",
            weight_kg: a.weight_kg || "",
            age: a.age || "",
            gender: a.gender || "prefer_not_to_say",
            fitness_level: a.fitness_level || "beginner",
            activity_frequency: a.activity_frequency || "moderate",
            health_conditions: a.health_conditions || [],
            goals: a.goals || [],
            reminder_time: a.reminder_time || "09:00",
          });
        }
      } catch (err) {
        // No prior assessment — expected for new users
        if (err.response?.status !== 404) devError("Assessment check failed:", err.message);
      }
    };
    checkExisting();
  }, []);

  const handleSubmit = async () => {
    if (!form.height_cm || !form.weight_kg) {
      toast.error(t("healthAssessment.enterHeightWeight"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/health-assessment`, {
        ...form,
        height_cm: parseFloat(form.height_cm),
        weight_kg: parseFloat(form.weight_kg),
        age: form.age ? parseInt(form.age) : null,
      });
      // Save reminder time to user settings
      try {
        await axios.put(`${API}/settings`, { reminder_time: form.reminder_time, daily_reminder: true });
      } catch (e) { devError("Settings save (non-critical):", e?.message); }
      setBmiResult(res.data);
      setStep(3);
      toast.success("Assessment saved!");
    } catch (err) {
      devError("Health assessment error:", err);
      toast.error(t("healthAssessment.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCondition = (condition) => {
    setForm(prev => {
      if (condition === "none") return { ...prev, health_conditions: ["none"] };
      const filtered = prev.health_conditions.filter(c => c !== "none");
      return {
        ...prev,
        health_conditions: filtered.includes(condition)
          ? filtered.filter(c => c !== condition)
          : [...filtered, condition]
      };
    });
  };

  const getBmiColor = (cat) => {
    switch (cat) {
      case "underweight": return "text-amber-500";
      case "normal": return "text-emerald-500";
      case "overweight": return "text-orange-500";
      case "obese": return "text-red-500";
      default: return "text-sky-500";
    }
  };

  const steps = [
    // Step 0: Body metrics
    <div key="body" className="space-y-5 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-sky-100"><Scale className="w-6 h-6 text-sky-500" strokeWidth={1.5} /></div>
        <div>
          <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: "Outfit" }}>{t("healthAssessment.bodyMetrics")}</h3>
          <p className="text-sm text-slate-500">{t("healthAssessment.bodyMetricsDesc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-sky-700 mb-1.5 block">{t("healthAssessment.height")}</Label>
          <Input type="number" value={form.height_cm} onChange={e => setForm(p => ({ ...p, height_cm: e.target.value }))}
            placeholder="175" className="border-sky-200 focus:border-sky-400" data-testid="height-input" />
        </div>
        <div>
          <Label className="text-sm text-sky-700 mb-1.5 block">{t("healthAssessment.weight")}</Label>
          <Input type="number" value={form.weight_kg} onChange={e => setForm(p => ({ ...p, weight_kg: e.target.value }))}
            placeholder="70" className="border-sky-200 focus:border-sky-400" data-testid="weight-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-sky-700 mb-1.5 block">{t("healthAssessment.age")}</Label>
          <Input type="number" value={form.age} onChange={e => setForm(p => ({ ...p, age: e.target.value }))}
            placeholder="30" className="border-sky-200 focus:border-sky-400" data-testid="age-input" />
        </div>
        <div>
          <Label className="text-sm text-sky-700 mb-1.5 block">{t("healthAssessment.gender")}</Label>
          <div className="grid grid-cols-2 gap-1.5">
            {GENDERS.map(g => (
              <button key={g} type="button" onClick={() => setForm(p => ({ ...p, gender: g }))}
                className={`text-xs px-2 py-1.5 rounded-lg transition-all ${form.gender === g
                  ? "bg-sky-400 text-white" : "bg-sky-50 text-sky-600 hover:bg-sky-100"}`}
                data-testid={`gender-${g}`}>
                {t(`healthAssessment.${g}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Daily reminder time */}
      <div className="mt-2 p-4 rounded-xl bg-sky-50 border border-sky-100">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
          <div className="flex-1">
            <Label className="text-sm text-sky-700 block">{t("healthAssessment.reminderTime") || "Daily check-in reminder"}</Label>
            <p className="text-xs text-slate-400">{t("healthAssessment.reminderTimeDesc") || "We'll remind you at this time each day"}</p>
          </div>
          <Input type="time" value={form.reminder_time} onChange={e => setForm(p => ({ ...p, reminder_time: e.target.value }))}
            className="w-28 border-sky-200 focus:border-sky-400 text-center" data-testid="onboarding-reminder-time" />
        </div>
      </div>
    </div>,

    // Step 1: Fitness level & activity
    <div key="fitness" className="space-y-5 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-indigo-100"><Activity className="w-6 h-6 text-indigo-500" strokeWidth={1.5} /></div>
        <div>
          <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: "Outfit" }}>{t("healthAssessment.fitnessProfile")}</h3>
          <p className="text-sm text-slate-500">{t("healthAssessment.fitnessProfileDesc")}</p>
        </div>
      </div>
      <div>
        <Label className="text-sm text-sky-700 mb-2 block">{t("healthAssessment.fitnessLevel")}</Label>
        <div className="grid grid-cols-2 gap-3">
          {FITNESS_LEVELS.map(fl => (
            <button key={fl.value} type="button" onClick={() => setForm(p => ({ ...p, fitness_level: fl.value }))}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${form.fitness_level === fl.value
                ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              data-testid={`fitness-${fl.value}`}>
              <span className="text-xl">{fl.icon}</span>
              <span className={`text-sm font-medium ${form.fitness_level === fl.value ? "text-indigo-700" : "text-slate-600"}`}>
                {t(`healthAssessment.${fl.value}`)}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-sm text-sky-700 mb-2 block">{t("healthAssessment.activityFrequency")}</Label>
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_LEVELS.map(al => (
            <button key={al.value} type="button" onClick={() => setForm(p => ({ ...p, activity_frequency: al.value }))}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${form.activity_frequency === al.value
                ? "bg-sky-400 text-white" : "bg-sky-50 text-sky-600 hover:bg-sky-100"}`}
              data-testid={`activity-${al.value}`}>
              {t(`healthAssessment.${al.value}`)}
            </button>
          ))}
        </div>
      </div>
    </div>,

    // Step 2: Health conditions
    <div key="health" className="space-y-5 animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-rose-100"><Heart className="w-6 h-6 text-rose-500" strokeWidth={1.5} /></div>
        <div>
          <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: "Outfit" }}>{t("healthAssessment.healthConditionsTitle")}</h3>
          <p className="text-sm text-slate-500">{t("healthAssessment.healthConditionsDesc")}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {HEALTH_CONDITIONS.map(hc => (
          <button key={hc} type="button" onClick={() => toggleCondition(hc)}
            className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${form.health_conditions.includes(hc)
              ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            data-testid={`condition-${hc}`}>
            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${form.health_conditions.includes(hc) ? "border-rose-400 bg-rose-400" : "border-slate-300"}`}>
              {form.health_conditions.includes(hc) && <Check className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-slate-700">{t(`healthAssessment.conditionOptions.${hc}`, hc)}</span>
          </button>
        ))}
      </div>
    </div>,

    // Step 3: BMI Result
    bmiResult && (
      <div key="result" className="space-y-6 animate-fade-in text-center">
        <div className="p-3 rounded-xl bg-emerald-100 inline-block mx-auto">
          <Check className="w-8 h-8 text-emerald-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-medium text-sky-800" style={{ fontFamily: "Outfit" }}>
          {t("healthAssessment.bmiResult")}
        </h3>
        <div className="glass-card p-6 inline-block">
          <p className={`text-5xl font-light ${getBmiColor(bmiResult.bmi_category)}`} style={{ fontFamily: "Outfit" }}>
            {bmiResult.bmi}
          </p>
          <p className="text-sm text-slate-500 mt-2 capitalize">{bmiResult.bmi_category}</p>
        </div>
        <p className="text-sm text-slate-500 max-w-sm mx-auto">
          {t("healthAssessment.bmiPersonalized")}
        </p>
        <Button onClick={() => navigate("/dashboard")} className="glow-button" data-testid="assessment-done-btn">
          {t("healthAssessment.goToDashboard")} <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    ),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white dark:from-slate-900 dark:to-slate-800 flex items-center justify-center px-4" data-testid="health-assessment-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light text-sky-900 dark:text-sky-100" style={{ fontFamily: "Outfit" }}>
            {t("healthAssessment.title")}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">{t("healthAssessment.subtitle")}</p>
        </div>

        {/* Progress dots */}
        {step < 3 && (
          <div className="flex justify-center gap-2 mb-8">
            {[0, 1, 2].map(i => (
              <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${i === step ? "bg-sky-400 scale-125" : i < step ? "bg-sky-300" : "bg-slate-200"}`} />
            ))}
          </div>
        )}

        <div className="glass-card p-6 sm:p-8">
          {steps[step]}

          {step < 3 && (
            <div className="flex items-center justify-between mt-8">
              {step === 0 ? (
                <button onClick={() => navigate("/dashboard")} className="text-sm text-slate-400 hover:text-slate-600" data-testid="skip-assessment">
                  {t("healthAssessment.skip", "Skip for now")}
                </button>
              ) : (
                <Button variant="ghost" onClick={() => setStep(s => s - 1)} data-testid="assessment-back">
                  {t("common.back")}
                </Button>
              )}
              {step < 2 ? (
                <Button onClick={() => setStep(s => s + 1)} className="glow-button" data-testid="assessment-next">
                  {t("common.next")} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} className="glow-button" data-testid="assessment-submit">
                  {submitting ? <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    : <>{t("healthAssessment.submit")} <Check className="w-4 h-4 ml-1" /></>}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthAssessment;

